import {
  mobileFilterRowEl,
  stickyBandBottom,
  stuckBottom,
} from "@/v2-components/pages/games/sticky-band";

/**
 * 검색을 **확정**하면 결과가 화면 맨 위에 오게 한다 (#5609 · 2026-09-08 사용자 지정:
 * *"검색 버튼 눌렀을때 딱 결과 위치부터 보이면 좋지 않을까? 딱 검색결과가 보이는 느낌"*).
 *
 * ## ★한 번 계산해서 한 번 스크롤하면 **반드시 어긋난다**
 *
 * 이 화면의 sticky 띠는 **스크롤 상태에 따라 자기 크기를 바꾼다**(390폭 실측 2026-09-08):
 *
 * ```
 *   scrollY 0     탭바 top 175 · h  48   (헤더 175 아래에 붙는다 · 도킹 줄 없음)
 *   scrollY 1872  탭바 top   0 · h 120   (헤더 위로 올라오고 검색+필터 줄 72 가 생긴다)
 * ```
 *
 * 즉 **스크롤이 도킹을 일으키고, 도킹이 흐름에 72px 을 끼워 넣어 아래 내용을 밀어낸다.**
 * 그래서 「지금」 잰 목표 위치는 도착할 때쯤 낡아 있다 — 실측으로 두 판 다 틀렸다:
 *
 * ```
 *   판1  window.scrollTo(anchor + scrollY - 헤더bottom)   결과바 top  0 · 72   (헤더 뒤로)
 *   판2  scrollIntoView + scrollMarginTop = 띠 높이        결과바 top 72 · 72   (정확히 +72)
 * ```
 *
 * 두 번째 판의 **72 는 도킹 줄의 높이 그 자체**다. 목표는 맞게 잡혔는데 도착 후 줄이
 * 끼어들어 앵커가 그만큼 밀렸다. 계산을 더 정교하게 해도 같은 되먹임에 걸린다.
 *
 * ## 그래서 **재정렬**한다 — 멈춘 뒤 다시 재서 남은 만큼만 더 민다
 *
 * 절대 좌표를 계산하지 않는다. **지금 화면에서 앵커가 띠 바닥에서 얼마나 떨어졌나**(`delta`)
 * 만 보고 그만큼 상대 스크롤한다. 멈추면 다시 재고, 어긋남이 1px 이하가 되면 끝낸다.
 * 도킹은 첫 패스에서 확정되므로 실제로는 두 패스면 수렴한다.
 *
 * 기준은 **탭바 바닥**이다(사용자 결정 2026-09-08). 확정은 곧 아래로 스크롤하는 동작이라
 * 그 직후 헤더(GNB)는 탭바 뒤로 들어간다 — 실질적인 「화면 위」가 거기다.
 */
/**
 * 결과 시작점 — 개수/정렬 바. 두 탭이 같은 표식을 쓴다.
 *
 * ## ⚠️ **VR 의 이 앵커는 `sticky` 다** — 이 정렬은 거기서 수렴하지 않는다
 *
 * #5561 이 VR 툴바를 검색줄 아래에 함께 고정했다(`vr-games-list.tsx`). 붙어 있는 동안
 * `getBoundingClientRect().top` 은 **그 sticky top 으로 고정**되므로 아래 `align` 의
 * `delta` 가 줄지 않는다:
 *
 * ```
 *   delta = rect.top − occludedHeight()
 *         = (띠바닥 + 검색줄높이) − 띠바닥
 *         = 검색줄 높이 ≈ 72        ← 스크롤해도 그대로다(고정점)
 * ```
 *
 * `|delta| <= 1` 이 참이 되지 않아 `MAX_PASSES`(4)를 다 태우고 매 패스 72px 씩 더 내려간다
 * → 약 **288px 과다 스크롤**. 이 함수가 막으려던 증상이 반대 방향으로 재발한다.
 *
 * ## ★위 고정점은 **c1 이 없앴다** (#5499 · 2026-09-08 · 이 문단을 다시 씀)
 *
 * 종전엔 *"지금은 발화하지 않는다 — 소비처가 한 곳(PC)이고 PC 앵커는 sticky 가 아니다"*
 * 라고 적혀 있었다. **두 가지가 틀렸다:**
 *
 * ```
 *   소비처   usePcSearchBox 는 **PC·VR 공용**이다 (vr/games.tsx 도 쓴다)
 *   앵커     VR 의 결과 툴바는 **sticky** 다 (vr-games-list.tsx · top = 검색줄 바닥)
 * ```
 *
 * 그런데도 비수렴이 **일어나지 않는다.** 위 산식의 전제(`occludedHeight` = 띠 바닥)가
 * c1 로 바뀌었기 때문이다 — 이제 **검색줄 바닥**을 세고, 그 값이 곧 VR 앵커의 sticky
 * `top` 이라 `delta = 0` 이 되어 **수렴**한다. 고정점이 소멸했다.
 *
 * ⚠️ **「delta 0 = 아예 안 움직인다」가 아니다.** sticky 는 **도달해야** 클램프되므로
 * 스크롤 전 `rect.top` 은 흐름 위치(먼 아래)다 — 첫 패스가 전 구간을 가고, 도착해서
 * 클램프된 뒤에 `delta = 0` 이 되어 멎는다. 실측(390 · dev · 2026-09-08):
 *
 * ```
 *          확정 전 scrollY   확정 후   이상적    과다   앵커
 *   VR          0            769      769       0     sticky
 *   PC          0           1241     1241       0     static
 * ```
 *
 * ★그래서 종전 주석이 남겨 둔 두 선택지(앵커를 아래 첫 요소로 / `offsetTop` 으로 재기)는
 * **지금 필요 없다.** 다만 `occludedHeight` 가 다시 「띠만」으로 돌아가면 고정점이
 * 되살아난다 — 그 되돌림은 `scroll-to-results.test.ts` 가 잠근다.
 */
export const RESULTS_ANCHOR_ATTR = "data-games-results";

/**
 * 결과 영역의 **흐름 기준 시작점** — 착지는 이걸 쓴다 (#5730).
 *
 * ## 왜 앵커를 하나 더 두나
 *
 * 위 `RESULTS_ANCHOR_ATTR`(개수·정렬 바)는 **VR 모바일에서 `sticky`** 다(#5561 이
 * 검색줄 아래에 함께 고정). 붙어 있는 동안 `getBoundingClientRect().top` 이 자기 `top`
 * 에 고정되므로 `align` 의 `delta` 가 줄지 않는다 — **고정점**이라 정렬이 못 움직인다.
 *
 * 실측(2026-09-11 · dev · 390×844 · `/games/vr/games/` · 아래로 스크롤해 붙인 뒤):
 *
 * ```
 *   앵커 position sticky · 앵커 vpTop 120
 *   모바일 줄 bottom 120  →  가림선 120
 *   delta = 120 − 120 = 0        ← 첫 패스에서 끝난다
 *   확정 결과: y 1680 → 1632 (48px · 갭 4패스분)  ?q=r · 카드 67→49
 * ```
 *
 * `vr-games-list.tsx` 의 그 줄 주석이 이 결함을 **예고해 뒀다** — *"sticky 가 되면서
 * `scrollToResults` 의 전제(앵커가 흐름에 있다)가 깨졌다"*.
 *
 * ## ★`offsetTop` 으로 흐름 위치를 재는 방법은 **폐기했다** — 되살리지 말 것
 *
 * 위 `RESULTS_ANCHOR_ATTR` 주석이 후보로 남겨 둔 방법인데 **성립하지 않는다.**
 * `offsetTop` 도 sticky 가 붙은 위치를 반영한다(실측):
 *
 * ```
 *   offsetTop 사슬 합 1752 − scrollY 1632 = 120 = rect.top
 *   chain:  1752  DIV.sticky z-[9]   ← 붙은 위치가 그대로 들어 있다
 *              0  DIV.relative w-full
 *              0  BODY
 * ```
 *
 * 그 방향으로 만든 가드는 **브라우저가 하지 않는 동작을 스텁**한 허위 확신이었다
 * (`offsetTop` 을 흐름 위치로 심었다). 그 메모 자체가 미검증 추측이었다.
 *
 * ## 그래서 **흐름에 있는 표식**을 둔다
 *
 * 높이 0 이라 레이아웃을 안 바꾸고, 위치만 쓴다. 부모를 기준으로 삼는 방법도 있지만
 * 그건 **「앵커가 첫 자식이고 패딩이 없다」**는 전제에 기댄다 — 센티널은 안 기댄다.
 *
 * ⚠️ **판정은 그대로 `RESULTS_ANCHOR_ATTR` 을 쓴다.** 판정(`useKeepResultsInView`)을
 * 같이 바꾸면 VR 모바일에서 **없던 발화가 생긴다**(지금은 붙은 툴바의 `bottom` 이 늘
 * 가림선보다 아래라 발화하지 않는다) — 이 이슈의 범위를 넘는다. 착지가 판정 요소의
 * **바로 위**에 놓이므로 되돌린 뒤 판정이 거짓이 되어 수렴한다.
 *
 * ⚠️ 0건에서도 안전하다 — 센티널은 **위치만** 쓰므로 그리드 높이가 0 인 것과 무관하다
 * (그리드를 착지에 쓰면 #5676 이 겪은 함정에 걸린다).
 */
export const RESULTS_START_ANCHOR_ATTR = "data-games-results-start";

/**
 * 로그인 복귀 후 **요청한 영역**을 화면에 올릴 때 쓰는 앵커 (#5536).
 *
 * ★위 ⚠️ 가 예고한 자리다 — 이 앵커는 **흐름에 있다**(빈 상태 루트,
 * `pc-games-empty.tsx`). 그래서 sticky 앵커의 비수렴에 걸리지 않고 아래 `align` 이
 * 그대로 쓰인다. 결과 툴바(`RESULTS_ANCHOR_ATTR`)를 쓰면 VR 에서 약 288px 과다
 * 스크롤이 난다.
 *
 * 빈 상태 **루트**를 잡는 이유: 사용자가 원한 것은 *"게임 요청한 부분이 잘 보이게"*
 * (#5536)인데, 투표 행만 화면 맨 위에 붙이면 그 위의 머리말·사유 문구가 잘려
 * **왜 투표하는지가 안 보인다.** 루트를 잡으면 이슈 스크린샷과 같은 구도
 * (머리말 → 사유 → 요청 행)가 된다.
 */
export const REQUEST_EMPTY_ANCHOR_ATTR = "data-game-request-empty";

/**
 * 이 앵커 위에 쌓인 sticky 층을 **다 센다** (#5636 리뷰 R1 M1).
 *
 * ## ★가림 항은 공유가 아니라 **앵커별**이다
 *
 * 첫 판은 두 앵커가 `stickyBandBottom`(띠만)을 함께 썼다. VR 모바일에는 띠 **아래로**
 * 층이 둘 더 있어(#5625 가 만든 스택) 정렬이 끝나도 머리말이 그 뒤에 숨는다.
 * 실측(2026-09-08 dev · 390 · **정착 후**):
 *
 * ```
 *   띠 바닥  48   검색줄 바닥 120   툴바 바닥 160
 *   앵커 top 48   머리말 top   72        ← 툴바 바닥 160 아래에 통째로 묶인다(112 가림)
 * ```
 *
 * 이 PR 이 앵커를 투표 행이 아니라 **루트**로 잡은 이유(*"머리말·사유가 잘려 왜
 * 투표하는지가 안 보인다"*)가 다른 경로로 무효화된다.
 *
 * ⚠️ **`delta → 0` 을 재는 것으로는 이걸 못 본다.** 그 수렴은 *"anchor.top ==
 * occludedHeight"* 를 말하는데, 가림 항 자체가 불완전하면 **어긋남 0 과 「머리말이
 * 보인다」가 다른 사실**이 된다. 첫 판의 3폭 수렴 실측이 그래서 통과했다.
 *
 * ⚠️ 그리고 그 실측을 **즉시 스크롤 직후에 읽으면 도킹 전 값이 섞인다** — 띠 바닥 223
 * 인데 검색줄 바닥 184 처럼 층 순서가 뒤집혀 나오고, 그 상태로 계산한 「가림 0」은 무효다.
 * 정착을 기다린 뒤 읽어야 위 표가 된다.
 *
 * ## ★PC 도 이제 같은 층 구조다 (#5499 · 2026-09-08 · 이 문단의 전제가 바뀌었다)
 *
 * 종전엔 *"PC 는 도킹 줄을 **탭바 안**에 넣어 띠 높이에 포함시키므로 `mobileFilterRowEl()`
 * 이 `null` → 띠만"* 이었다. #5499 가 **그 도킹을 걷어** 줄을 띠 **밖**으로 냈다(도킹이
 * 띠 높이를 바꿔 스크롤이 튀었다). 그래서 지금은 PC 모바일에서도 그 함수가 요소를
 * 돌려주고, 이 계산이 **두 탭에 다 걸린다.**
 *
 * ⚠️ 갈리는 축이 「PC/VR」이 아니라 **「그 줄이 지금 덮고 있나」**다:
 *
 * ```
 *                   데스크탑에서                     mobileFilterRowEl()
 *   VR   laptop:hidden   display:none                 null  (보이지 않음)
 *   PC   laptop:static   **보이지만 고정이 아니다**     null  (고정이 아님)
 * ```
 *
 * 두 술어를 다 봐야 한다 — 그 판정은 `sticky-band.ts` 한 곳에 있다.
 */
function occludedForRequestEmpty(): number {
  const row = mobileFilterRowEl();
  if (!row) return stickyBandBottom();
  const toolbar = document.querySelector<HTMLElement>(
    `[${RESULTS_ANCHOR_ATTR}]`,
  );
  return Math.max(stuckBottom(row), toolbar ? stuckBottom(toolbar) : 0);
}

/**
 * 요청 영역을 화면 위로 (#5536). **앵커와 그 가림 항을 한 자리에 묶는다** —
 * 호출처가 `scrollToAnchor` 를 직접 부르면 기본 가림 항(띠만)이 조용히 적용돼
 * 위 M1 이 재발한다.
 */
export function scrollToRequestEmpty() {
  scrollToAnchor(REQUEST_EMPTY_ANCHOR_ATTR, occludedForRequestEmpty);
}

/**
 * **바깥 재귀**(`align` → `onScrollSettled` → `align`)의 상한. 결과가 적어 **더 스크롤할
 * 수 없으면** `delta` 가 0 이 되지 않으므로 횟수로 끊는다 — 그 경우 목록이 이미 한 화면에
 * 다 들어와 있다.
 *
 * ⚠️ 종전 주석은 이것을 *"무한 루프 방지"* 라고 적었는데 **안쪽 rAF 루프는 안 덮는다**
 * (리뷰 R5 🔵 j2). 안쪽은 아래 `MAX_SETTLE_FRAMES` 가 따로 끊는다.
 */
const MAX_PASSES = 4;

/**
 * **안쪽 대기 루프**(`tick`)의 프레임 상한 ≈ 2초(60fps).
 *
 * 페이지가 계속 미세하게 움직이면(모멘텀 · CSS 애니메이션) `still` 이 3에 못 닿아 무기한
 * 돈다. 사용자 입력이 없으면 `aborted` 도 안 서므로 **프레임으로도 끊는다** — 취소 경로가
 * 사용자 입력 하나뿐이면 그게 없는 경우가 그대로 남는다.
 */
const MAX_SETTLE_FRAMES = 120;

/**
 * 화면 위를 **가리는 높이** — 붙어 있는 **가장 아래 층의 바닥**이다.
 *
 * ★**층이 하나가 아니다** (리뷰 R1 🟡 c1 · 2026-09-08). `sticky-band.ts` 가 정의하듯
 * 이 페이지의 sticky 는 쌓인다:
 *
 * ```
 *   띠(서브내비)              top = 헤더 높이(스크롤에 따라 0)
 *   검색·필터 줄(모바일)       top = 띠의 stuckBottom          ← PC 는 #5499 에서 추가
 * ```
 *
 * 종전엔 띠 **한 층만** 셌다. PC 모바일에서는 그게 맞았다 — 검색줄이 띠 **안**에
 * 있어서 띠 높이에 포함됐다(도킹). #5499 가 줄을 띠 **밖으로** 내면서 띠는 48 이 되고
 * 줄이 `[48, 120]` 을 따로 덮는다. 그대로 두면 확정 스크롤이 결과 바를 48 에 맞추고
 * **검색줄이 그 위를 덮는다** — 실측(390 · 고치기 전):
 *
 * ```
 *   띠 bottom 48 · 검색줄 [48,120] · 결과바 top 48   → **72px 완전히 가려짐**
 * ```
 *
 * 공식과 그 근거(`rect.bottom` 을 쓰면 안 되는 이유)는 `sticky-band.ts` 에 있다.
 *
 * ★**내보낸다** (#5676). `useKeepResultsInView` 가 *「결과가 이 선 위로 완전히
 * 벗어났나」* 를 판정하는 데 같은 값을 써야 한다. 그쪽이 층을 다시 세면 **가림 항이
 * 두 벌**이 되고, 판정과 착지가 서로 다른 선을 보게 된다.
 */
export const occludedHeight = () => {
  const row = mobileFilterRowEl();
  return row ? stuckBottom(row) : stickyBandBottom();
};

/**
 * 사용자가 스크롤을 가져가는 신호.
 *
 * ⚠️ `scroll` 이벤트를 쓰면 안 된다 — **우리 스크롤도 그걸 쏜다.** 사용자 입력만 나오는
 * 이벤트라야 프로그램 스크롤과 갈린다.
 */
const USER_SCROLL_EVENTS = ["wheel", "touchstart", "keydown"] as const;

/**
 * 스크롤이 **멎을 때까지** 기다린다. 셋 중 하나로 끝난다 — 멎음 · 사용자 입력 · 프레임 상한.
 *
 * `scrollend` 이벤트를 쓰지 않는다 — 브라우저 지원이 갈리고, 스크롤이 아예 안 일어나면
 * (더 내려갈 곳이 없을 때) 발화하지 않아 콜백이 유실된다. 프레임마다 `scrollY` 를 보고
 * 세 프레임 연속 같으면 멎은 것으로 본다.
 *
 * ## ★사용자가 스크롤을 가져가면 **재정렬을 포기한다** (리뷰 R5 🟡 j1)
 *
 * 「멎었나」만 보면 **누가 멈췄는지**를 모른다. 확정 직후 손으로 훑어보는 흔한 동작에서:
 *
 * ```
 *   1  확정 → smooth 스크롤 시작
 *   2  사용자가 아래로 훑는다 (브라우저가 smooth 를 취소한다)
 *   3  scrollY 가 계속 바뀌어 `still` 이 리셋 → 루프는 살아 있다
 *   4  읽으려고 멈추는 순간 → 3프레임 안정 → **앵커로 되돌린다**
 * ```
 *
 * 정렬이 덜 된 채로 두는 편이 **사용자가 고른 위치를 빼앗는 것보다 낫다.**
 */
function onScrollSettled(fn: () => void) {
  let last = window.scrollY;
  let still = 0;
  let frames = 0;
  let aborted = false;

  const abort = () => {
    aborted = true;
  };
  const opts = { passive: true } as const;
  USER_SCROLL_EVENTS.forEach((e) => window.addEventListener(e, abort, opts));
  const cleanup = () =>
    USER_SCROLL_EVENTS.forEach((e) => window.removeEventListener(e, abort));

  const tick = () => {
    if (aborted || ++frames > MAX_SETTLE_FRAMES) return cleanup();

    const y = window.scrollY;
    still = y === last ? still + 1 : 0;
    last = y;
    if (still >= 3) {
      cleanup();
      fn();
    } else requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

/** 앵커를 찾는 방법. 속성 셀렉터와 **요소 자체** 둘 다 이 형태로 들어온다. */
type FindAnchor = () => HTMLElement | null;

/**
 * 정렬이 일으키는 스크롤은 **사용자 의도가 아니다** (#5672).
 *
 * ## 무엇이 있었나
 *
 * 아래 `align` 은 결과를 띠 아래로 되돌리려고 **위로** 스크롤한다. 그런데 게임 탭의
 * 헤더는 `useHiddenOnScroll` 로 자동 숨김이라 「위로 스크롤 = 헤더를 다시 보여라」로
 * 읽는다. 헤더가 나오면 `GamesPageTemplate` 의 `stickyTop` 이 `0 → headerHeight` 로
 * 바뀌고, **스티키 띠가 그만큼 통째로 내려간다** — 그 안에 도킹된 검색창이 한 프레임에
 * 뛰어내린다.
 *
 * 실측(2026-09-10 · dev · 1440×900 · `/games/pc/games/` · y=1500 에서 `h` 한 글자):
 *
 * ```
 *   t=732  y=1499  검색창 vpTop=  4  bandTop=  0  stickyTop=  0px
 *   t=864  y=1269  검색창 vpTop=141  bandTop=137  stickyTop=137px   ← ★한 프레임 +137
 * ```
 *
 * ⚠️ **도킹 해제가 아니다.** 점프 전후로 `document.activeElement` 가 **같은 `<input>`**
 * 이고 포커스도 유지된다(`h` 뒤에 `a` 를 치면 `ha`) — 노드가 교체되지 않았다는 뜻이다.
 * 「포커스 유실」이나 「슬롯 교체」로 진단하면 안 고쳐진다.
 *
 * ## 표식만 남기고 판정은 소비처가 한다
 *
 * 여기서 `useHiddenOnScroll` 을 직접 건드리지 않는다 — 그 훅은
 * `GameDetailPageTemplate` 도 쓰고, 그쪽 프로그램적 스크롤(`TableOfContents` 의 목차
 * 클릭)은 **사용자가 방금 누른 것**이라 헤더가 나오는 게 맞다. 그래서 이 모듈은
 * 「지금 내가 스크롤 중」이라는 사실만 알리고, 그 사실을 **쓸지 말지는 템플릿이 정한다**
 * (`GamesPageTemplate` 만 넘긴다).
 * ⚠️ 그 목차(`TableOfContents`)는 게임 상세 개편(#5476)으로 렌더에서 빠졌고 2026-10-01 에
 * 파일째 지웠다 — 지금 게임 상세엔 프로그램적 스크롤이 없다. 비대칭은 그대로 둔다.
 *
 * ## 시간으로 끊는 이유
 *
 * `behavior: "smooth"` 의 종료는 직접 관측할 수 없다. `scrollend` 는 지원이 고르지 않고
 * (Safari 늦음), 「스크롤이 N ms 조용하면」은 사용자의 스크롤과 섞인다. 그래서 마지막
 * 프로그램적 호출로부터 고정 창을 둔다 — `align` 은 여러 패스로 나뉘므로 호출마다
 * 창을 **갱신**한다.
 *
 * ## 대가 둘 — 받아들인 것이다 (R1 z2)
 *
 * ⚠️ **억제 시간은 700ms 가 아니라 「마지막 패스 + 700ms」다.** 패스마다 갱신되므로
 * `MAX_PASSES`(4) × 정착 대기(`MAX_SETTLE_FRAMES` 120프레임 ≈ 2s)를 다 태우면 수 초까지
 * 간다. 실제로는 `sign` 반전 조기 종료로 보통 두 패스에서 끝난다(위 `align` 주석).
 *
 * ⚠️ **그 동안 사용자 자신의 스크롤도 헤더를 못 움직인다 — 출처를 가리지 않는다.**
 * 검색어를 치다가 결과를 훑으려고 스크롤하면 헤더 자동 숨김이 한 박자 늦는다.
 * 137px 점프보다 나은 대가로 받아들인다.
 *
 * ⚠️ 이 파일에는 사용자 입력을 가리는 술어가 이미 있다(`USER_SCROLL_EVENTS` —
 * `wheel`·`touchstart`·`keydown`). 그걸 여기 쓰지 않은 이유는 **정렬 취소와 헤더 억제가
 * 서로 다른 사건**이기 때문이다: 사용자가 스크롤을 가져가면 정렬이 **포기**하고
 * (`aborted` · `:248`) `align` 재진입이 없으므로 창이 **마지막 호출 + 700ms 에** 만료된다
 * — 즉시가 아니라 **최대 700ms 늦다.** 두 판정을 합치면 「방어를 둘 두면 서로의 뮤턴트를
 * 가린다」가 되고, 그 700ms 는 위 두 대가와 같은 성격이라 함께 받아들인다.
 *
 * 확인: 이 표식을 **부르는 자리는 `align` 안 한 곳뿐**이다. 그래서 정렬이 멎으면 창이
 * 더 갱신되지 않는다.
 *
 * ⚠️ 그 사실을 grep 으로 세려면 **주석을 걷어내고** 세야 한다 — 이 문단 자체가 그 이름을
 * 담고 있어 맨 grep 은 자기 자신을 센다(이 세션에서 같은 기전을 네 번 만났다). 가드는
 * `__tests__/hooks/hidden-on-scroll-programmatic.test.tsx` 가 파서로 걷어낸 소스에서
 * 표식과 `scrollBy` 의 **순서**를 단언한다.
 */
const PROGRAMMATIC_SCROLL_MS = 700;

let programmaticUntil = 0;

/** 「지금부터 잠시는 내가 스크롤한다」를 알린다. 호출마다 창이 갱신된다. */
export const markProgrammaticScroll = () => {
  programmaticUntil = Date.now() + PROGRAMMATIC_SCROLL_MS;
};

/**
 * 프로그램적 스크롤 창 안인가.
 *
 * ⚠️ 소비처는 이걸 **함수로** 받아야 한다 — 값으로 읽어 넘기면 그 순간의 스냅샷이라
 * 스크롤 이벤트마다 다시 묻지 못한다.
 */
export const isProgrammaticScroll = () => Date.now() < programmaticUntil;

function align(
  pass: number,
  from: string,
  find: FindAnchor,
  occluded: () => number,
  prevSign = 0,
  behavior: ScrollBehavior = "smooth",
) {
  /**
   * ⚠️ **떠난 화면에서는 발화하지 않는다** (리뷰 R5 🔵 j2). PC 와 VR 이 **같은 앵커
   * 표식**을 쓰므로, 탭을 옮기는 중에 남은 패스가 돌면 새 탭의 앵커를 잡아 **사용자가
   * 요청하지 않은 스크롤**이 일어난다. 노드 동일성으로는 못 가른다 — 목록이 그리드↔빈
   * 상태로 갈릴 때 그 줄이 다시 마운트될 수 있어 정상 수렴까지 끊긴다.
   */
  if (window.location.pathname !== from) return;

  const anchor = find();
  if (!anchor) return;

  const delta = Math.round(anchor.getBoundingClientRect().top - occluded());
  if (Math.abs(delta) <= 1 || pass >= MAX_PASSES) return;

  markProgrammaticScroll();
  window.scrollBy({ top: delta, behavior });

  /**
   * ★**방향이 뒤집히면 이 보정을 마지막으로 끝낸다** (#5636 리뷰 R1 M1 반영 중 실측).
   *
   * 위 ⚠️ 는 도킹 줄(72)만 다루는데, **헤더(GNB · 175)에도 같은 되먹임이 있고 그쪽은
   * 스크롤 방향에 달렸다** — 내리면 접히고 올리면 펴진다. 그래서 한 번 지나치면 보정이
   * 부호를 바꿔 오가며 수렴하지 않는다. 실측(2026-09-08 dev · VR 390 · 정착 대기 4패스):
   *
   * ```
   *   패스1  delta  +705   scrollY 0   → 705
   *   패스2  delta  −111           705 → 594
   *   패스3  delta  +175           594 → 769
   *   패스4  delta  −175           769 → 594     ← ±175 왕복(= 헤더 높이)
   * ```
   *
   * 문서 끝에 닿은 것이 **아니다**(최대 scrollY 1726). 그래서 횟수 상한만으로 끊으면
   * 사용자에게 **눈에 보이는 왕복 두 번**이 그대로 보인다.
   *
   * 첫 역방향 보정까지는 **해야 한다** — 그게 「가림 뒤에서 끌어내는」 방향이다(패스2 전
   * 앵커는 가림 바닥보다 111 위 = 숨어 있다). 그 뒤부터가 왕복이므로 거기서 멎는다.
   * 결과(패스2 종료 · scrollY 594): 머리말 가려짐 **0** · 요청 행 화면 안 — 목적 달성.
   *
   * 딱 맞게 붙이지 못하고 여유가 남는 것을 **받아들인다.** 정렬은 「가려지지 않게」가
   * 목적이고, 픽셀 정렬을 더 밀면 위 왕복을 다시 만난다.
   */
  const sign = Math.sign(delta);
  if (prevSign !== 0 && sign !== prevSign) return;
  onScrollSettled(() => align(pass + 1, from, find, occluded, sign, behavior));
}

/**
 * 앵커를 **그 앵커의 가림 항 바닥**에 맞춘다. 재정렬 규약은 하나로 공유하되
 * **가림 항은 앵커별**이다 — 층 스택이 화면마다 다르기 때문이다(R1 M1 · 위
 * `occludedForRequestEmpty` 주석의 실측표). 규약을 각자 구현하면 「도킹이 흐름을
 * 밀어낸다」는 되먹임을 각자 다시 만난다.
 *
 * ★**기본값이 「띠만」에서 「붙어 있는 가장 아래 층」으로 바뀌었다** (#5499 c1).
 * 종전엔 `stickyBandBottom` 이었는데, PC 가 도킹을 걷으면서 검색줄이 띠 **밖**의 별
 * 층이 됐고 그대로 두면 결과 바가 **그 줄 뒤로** 착지한다(실측: 72px 완전히 가려짐).
 *
 * ⚠️ 그래도 **앵커 자신(`결과 툴바`)의 `stuckBottom` 은 쓰지 않는다** — 그건 위 ⚠️ 의
 * **고정점**(비수렴)이 된다. 세는 것은 그 **위** 층까지다(검색줄 바닥). 실측(390 ·
 * 고친 뒤): PC·VR 둘 다 결과바 120 = 검색줄 바닥 120 · 가림 0 · 과다 스크롤 0.
 */
/**
 * 「필터 줄부터」 — 장르를 달고 도착했을 때 맞추는 앵커 (#5645).
 *
 * 작성자 요청(2026-09-09): *"PC메인에서 장르 선택시, 게임 페이지에서 해당 장르 필터링하여
 * 보여줄 때 페이지의 필터부분부터 보여주는거 가능할까요?"*
 *
 * ## ★가림 항이 **띠만**이다 — 기본값을 쓰면 안 된다
 *
 * 위 `occludedHeight`(기본값)는 **모바일 검색·필터 줄까지** 센다. 그 줄이 **가리는 것**
 * 이라서 맞는 계산인데, 이 앵커는 **그 줄을 보여주는 것**이 목적이라 방향이 반대다.
 * 기본값을 쓰면 필터 줄을 지나쳐 그 아래에 착지한다.
 *
 * ## ★앵커는 줄 자신이 아니라 **그 부모**다
 *
 * 검색·필터 줄(`PcGamesList.tsx:150`)은 모바일에서 `sticky` 라, 거기에 맞추면 위 ⚠️ 의
 * **고정점**(비수렴)이 된다. 같은 top 을 갖는 흐름 안의 부모(`PC_GAMES_CONTAINER` 가
 * 붙는 `list-module` 컨테이너)를 잡으면 프레이밍은 같고 수렴한다.
 */
export const FILTERS_ANCHOR_ATTR = "data-games-filters";

/**
 * 착지 여백 — 띠에 **딱 붙지 않게** 한다 (작성자 2026-09-09: *"all 부터 서치까지 있는
 * 섹션이 상단 탭과 붙어 있다. 갭이 있으면 좋겠다"*).
 *
 * 값의 근거는 작성자가 든 선례다 — *"서치도 모바일에서 스티키 할때 간격처리를 해줬거든"*.
 * 그 줄이 `py-3`(12)을 갖는다(`PcGamesList.tsx` 의 검색·필터 줄).
 *
 * ## ★여기서 주는 이유 — CSS 로는 못 준다
 *
 * 아래 `scrollUnderBand` 주석은 *"착지 여백은 목적지 섹션이 CSS 로 갖는다"* 이고 종전에
 * `gap` 파라미터를 **소비처 0곳**이라 걷었다. 이 앵커에서는 그 규약이 안 선다:
 *
 * - 이 루프는 `scrollBy` 라 **`scroll-margin-top` 이 발동하지 않는다**
 * - 앵커 컨테이너에 `pt-` 를 주면 **평소 레이아웃도 12px 늘어난다** — 착지에만 필요한
 *   여백인데 상시 간격을 바꾸는 것은 대가가 다르다
 *
 * 그래서 「가림 항 + 여백」으로 준다.
 *
 * ## ★소비처가 **둘**이 됐다 — 그래서 값을 공유한다 (#5672)
 *
 * 종전 주석은 *"`gap` 파라미터를 일반화하지 않고 이 함수 안에 두는 이유는, **다른
 * 앵커가 필요해지기 전까지** 소비처가 하나이기 때문"* 이라고 적었다. 그 전제가 바뀌었다 —
 * 사용자 지정(2026-09-10): *"Enter 로 이동시 `Total 0 games` 부터 시작하는 줄이 너무
 * 상단에 붙는데 상단갭이 좀 있으면 좋겠다"*.
 *
 * 실측(고치기 전 · 1440×900 · `hal` 확정 후):
 *
 * ```
 *   결과바 top 49  ·  띠 bottom 49  →  갭 **0**   ← 딱 붙는다
 * ```
 *
 * **값을 두 벌로 두지 않는다.** 같은 화면·같은 성격의 여백이므로 한쪽만 고쳐지는 사고가
 * 나면 두 착지가 갈린다. 그래서 이름에서 `FILTERS_` 를 뺀다.
 */
export const LANDING_GAP = 12;

/**
 * 이 앵커의 가림 항. **테스트가 값으로 관측할 수 있게** 내보낸다 — 형제
 * `takePendingVote` 를 내보낸 것과 같은 이유다(소스 텍스트를 스캔하는 대신 동작을 잰다).
 *
 * 종전엔 가드가 `scrollToAnchor(FILTERS_ANCHOR_ATTR, stickyBandBottom)` 라는 **표기**를
 * 못박았는데, 여백을 넣어 화살표 함수가 되자 그 자리에서 깨졌다 — 표기를 바꿀 때마다
 * 깨지는 단언은 계약이 아니라 스냅샷이다.
 */
export const occludedForFilters = () => stickyBandBottom() + LANDING_GAP;

export function scrollToFilters() {
  scrollToAnchor(FILTERS_ANCHOR_ATTR, occludedForFilters);
}

export function scrollToAnchor(
  attr: string,
  occluded: () => number = occludedHeight,
) {
  startAlign(() => document.querySelector<HTMLElement>(`[${attr}]`), occluded);
}

/**
 * ★**임의의 요소를 가림 층 바로 아래로** — 같은 재정렬 루프를 쓴다 (#5551 · 2026-09-08).
 *
 * 어드민 배너 CTA 의 해시 링크(`#pc-tactsuit-lineup`)가 같은 페이지의 그 섹션으로
 * 내려가는 데 쓴다. 목적지가 **어드민이 정하는 임의 id** 라 속성 셀렉터로는 못 잡는다.
 *
 * 처음엔 그쪽에 `scrollIntoView` + `scrollMarginTop = stickyBandBottom()` 을 따로
 * 썼는데 **137px 어긋났다** — 클릭 시점의 띠 바닥(186 · 헤더 보임)이 도착 시점(49 ·
 * 헤더 숨음)엔 낡기 때문이다. 위 루프가 이미 푼 되먹임과 **같은 것**이라 공식을 두 벌로
 * 두지 않는다.
 *
 * ## ★`isConnected`(노드 동일성)를 여기서는 쓴다
 *
 * 속성 경로는 매 패스 **다시 조회**한다 — 결과 툴바처럼 리마운트되는 앵커 때문이다.
 * 이 함수의 대상은 **조건부 렌더가 없는 섹션**(`PcProductLineup` 의 최상위 `<section>`)
 * 이라 노드를 잡고 있어도 정상 수렴이 안 끊긴다.
 *
 * ⚠️ 리마운트되는 요소에 이 함수를 쓰면 **첫 패스 뒤 조용히 멈춘다.** 그런 대상이
 * 생기면 속성 경로(`scrollToAnchor`)를 쓴다.
 *
 * ⚠️ **착지 여백은 여기서 주지 않는다.** 목적지 섹션이 CSS 로 갖는다
 * (`PcProductLineup` 의 `pt-6 laptop:pt-8`). 종전엔 `gap` 파라미터가 있었는데 비-0 을
 * 넘기는 호출처가 **0곳**이라 걷었다(리뷰 R2 🔵 d3).
 */
export function scrollUnderBand(el: HTMLElement) {
  startAlign(() => (el.isConnected ? el : null), occludedHeight);
}

/**
 * 요소를 **화면 위에서 `top` px** 자리에 **즉시** 맞춘다 — 떠날 때 보던 위치로 되돌리기
 * (`use-games-list-return-anchor`). 재정렬 규약(멈추면 다시 재기 · 방향이 뒤집히면 끝)은 위와 같고,
 * 가림선 대신 **떠날 때 잰 그 높이**가 목표다.
 *
 * 검색 확정(`scrollUnderBand`)과 달리 **스크롤 과정을 보이지 않는다**(2026-10-20 작성자 요청: *"스크롤
 * 과정없이 바로 보이게"*). 「돌아왔다」는 원래 그 자리에 있던 것이지 이동이 아니라서다.
 * - `behavior: "instant"` — 부드러운 이동 없음
 * - 첫 패스를 **동기로** 돈다(아래 `startAlign` 의 rAF 두 번을 건너뛴다) — 호출처가 그리기 전
 *   (`useLayoutEffect`)에 부르면 맨 위 화면이 한 번도 안 그려진다
 * - 뒤 패스(도킹·헤더가 흐름을 밀어낸 만큼의 보정)도 즉시라 미끄러짐 없이 한 번에 붙는다
 */
export function scrollToViewportTop(el: HTMLElement, top: number) {
  if (typeof window === "undefined") return;
  align(
    0,
    window.location.pathname,
    () => (el.isConnected ? el : null),
    () => top,
    0,
    "instant",
  );
}

function startAlign(find: FindAnchor, occluded: () => number) {
  if (typeof window === "undefined") return;
  const from = window.location.pathname;
  /**
   * ⚠️ **렌더 뒤로 미룬다.** `submit()` 안에서 동기로 부르면 그 시점엔 오버레이가 아직
   * 닫히지 않았고 목록도 새 결과로 다시 그려지기 전이다. `requestAnimationFrame` 을
   * **두 번** 쓴다 — 한 번은 React 커밋 전에 돌 수 있어서 두 번째 프레임이라야 새
   * 레이아웃에서 잰다.
   */
  requestAnimationFrame(() =>
    requestAnimationFrame(() => align(0, from, find, occluded)),
  );
}

/** 검색을 확정했을 때 — 결과 툴바를 화면 위로. 종전 호출 규약을 그대로 둔다. */
/**
 * 결과 앵커의 가림 항 — **가림 층 + 착지 갭** (#5672).
 *
 * ⚠️ `occludedHeight`(기본값)와 갈라 두는 이유: 그 함수는
 * `useKeepResultsInView` 의 **발화 판정**도 쓴다. 판정선에 갭을 더하면 *"결과 바가
 * 갭만큼 보이는 상태"* 도 「벗어났다」로 읽어 발화 범위가 넓어진다 — 착지에만 필요한
 * 여백이 판정을 건드리면 안 된다.
 *
 * 즉 **판정은 갭 없는 선 · 착지는 갭 있는 선**이고, 착지가 판정선보다 아래이므로
 * 되돌린 뒤 조건이 거짓이 되어 수렴한다(진동하지 않는다).
 *
 * **테스트가 값으로 관측할 수 있게** 내보낸다 — 형제 `occludedForFilters` 와 같은 이유다.
 */
export const occludedForResults = () => occludedHeight() + LANDING_GAP;

export function scrollToResults() {
  scrollToAnchor(RESULTS_START_ANCHOR_ATTR, occludedForResults);
}
